import React, { Suspense, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Switch } from 'antd';
import { OrbitControls, Environment, Loader, Html, Center } from '@react-three/drei';
import { observer } from 'mobx-react-lite';

import { Edges } from './Edges';
import { Grid } from './Grid';
import { Viewcube } from './ViewCube';
import { ContactShadows } from './ContactShadows';
import { Canvas, useThree, useFrame } from '@react-three/fiber';

import QuickTurnPartStore from '../../../stores/QuickTurnPartStore';
import PortalLoading from 'components/PortalLoading';
import { useWindowSize } from '@partsbadger/utils';

interface Props {
    showViewCube?: boolean;
    showGrid?: boolean;
    showDimensions: boolean;
    borderRadius?: string;
}

const ThreeJSCanvas = (props: Props) => {
    const { outerDiameter, hasThruHole, hasFlange, flangeDiameter } = QuickTurnPartStore;

    const material = QuickTurnPartStore?.materials?.find(x => x.id === QuickTurnPartStore?.material);

    const { width } = useWindowSize();

    const [scene, setScene] = useState<THREE.Scene>();
    const [geometry, setGeometry] = useState<THREE.BufferGeometry>();
    const [showDimensions, setShowDimensions] = useState<boolean>(props.showDimensions);
    const [overallLengthDimensionVisible, setOverallLengthDimensionVisible] = useState<boolean>(false);
    const [overallLengthInputFocused, setOverallLengthInputFocused] = useState<boolean>(false);
    const [renamePartModalVisible, setRenamePartModalVisible] = useState<boolean>(false);

    const overallLengthInputRef = useRef();

    const loader = new THREE.BufferGeometryLoader();

    const gridConfig = {
        gridSize: [10.5, 10.5],
        cellSize: 0.5,
        cellThickness: 1,
        cellColor: '#6f6f6f',
        sectionSize: 5,
        sectionThickness: 1.5,
        sectionColor: '#7f7f7f',
        fadeDistance: 30,
        fadeStrength: 1,
        followCamera: false,
        infiniteGrid: true,
    };

    async function fetchPart() {
        if (QuickTurnPartStore.partToken) {
            await QuickTurnPartStore.getPartByID(QuickTurnPartStore.partToken);
        } else {
            await QuickTurnPartStore.startNewPart();
        }
    }

    function Scene() {
        const threejs = useThree();
        setScene(threejs?.scene);

        return null;
    }

    useEffect(() => {
        fetchPart();
    }, []);

    useEffect(() => {
        if (width && width <= 480) {
            setShowDimensions(false);
        }
    }, [width]);

    useEffect(() => {
        if (QuickTurnPartStore.bufferGeometry) {
            const bufferGeo = loader.parse(JSON.parse(QuickTurnPartStore.bufferGeometry));
            setGeometry(bufferGeo);
        }
    }, [QuickTurnPartStore.bufferGeometry]);

    return (
        <>
            {!QuickTurnPartStore.partToken ||
            !QuickTurnPartStore.bufferGeometry ||
            !QuickTurnPartStore.materials ||
            !material ? (
                <PortalLoading />
            ) : (
                <div id="quickturn-3d-canvas-container">
                    <div
                        className="mb-2"
                        style={{
                            position: 'relative',
                            paddingBottom: '75%',
                            zIndex: 0,
                            borderRadius: props?.borderRadius ? props.borderRadius : '0.25rem',
                            background: 'radial-gradient(circle, rgba(82,89,94,1) 0%, rgba(46,53,60,1) 100%)',
                        }}
                    >
                        <div
                            className="d-flex align-items-center justify-content-end"
                            style={{ columnGap: '6px', position: 'absolute', padding: '1rem', zIndex: 9 }}
                        >
                            <Switch
                                size="small"
                                checked={showDimensions}
                                onChange={value => setShowDimensions(value)}
                            />
                            <span style={{ color: '#ccc', userSelect: 'none' }}>Show Dimensions</span>
                        </div>
                        <Suspense fallback={null}>
                            <Canvas
                                dpr={window.devicePixelRatio}
                                camera={{ fov: 18, near: 0.1, far: 1000, position: [-8, 3, 10] }}
                                shadows={true}
                                className="rounded-sm"
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                }}
                            >
                                {/* <color attach="background" args={[0xffffff00]} /> */}
                                <Suspense fallback={null}>
                                    <Scene />

                                    {width && width > 480 && props?.showViewCube !== false ? (
                                        <Suspense fallback={null}>
                                            <Viewcube />
                                        </Suspense>
                                    ) : null}

                                    <hemisphereLight intensity={0.5} position={[0, 200, 0]} />
                                    <directionalLight
                                        color={0xbbbbbb}
                                        intensity={0.5}
                                        position={[6, 50, -12]}
                                        // shadow={[200, 200, 200]}
                                    />

                                    <Environment files="/hdr/empty_warehouse_01_1k.hdr" />

                                    <pointLight position={[10, 10, 10]} intensity={0.5} />
                                    <Center>
                                        {showDimensions ? (
                                            <>
                                                {/* {overallLengthInputFocused ? <OverallLengthDimension /> : null} */}
                                                <OverallLengthDimension />
                                                <OuterDiameterDimension />
                                                {hasThruHole ? <InnerDiameterDimension /> : null}
                                                {hasFlange ? (
                                                    <>
                                                        <FlangeDiameterDimension />
                                                        <FlangeThicknessDimension />
                                                    </>
                                                ) : null}
                                            </>
                                        ) : null}

                                        <group>
                                            <mesh geometry={geometry} castShadow>
                                                <meshPhysicalMaterial
                                                    attach="material"
                                                    color={material?.threejs_mat_color}
                                                    metalness={material?.threejs_mat_metalness}
                                                    roughness={material?.threejs_mat_roughness}
                                                />
                                                <Edges
                                                    scale={1.002}
                                                    threshold={15} // Display edges only when the angle between two faces exceeds this value (default=15 degrees)
                                                    color={
                                                        QuickTurnPartStore?.materials?.find(x => x.id === material?.id)
                                                            ?.threejs_edges_color || 0x444444
                                                    }
                                                />
                                            </mesh>
                                        </group>
                                    </Center>
                                    <ContactShadows
                                        position={[
                                            0,
                                            hasFlange ? -(flangeDiameter / 2) : -(outerDiameter / 2) - 0.01,
                                            0,
                                        ]}
                                        opacity={0.5}
                                        scale={10}
                                        blur={1}
                                        // far={0.8}
                                    />
                                    {props?.showGrid !== false ? (
                                        <Grid
                                            position={[
                                                0,
                                                hasFlange ? -(flangeDiameter / 2) : -(outerDiameter / 2) - 0.01,
                                                0,
                                            ]}
                                            {...gridConfig}
                                        />
                                    ) : null}

                                    <OrbitControls
                                        dampingFactor={0.3}
                                        minDistance={4}
                                        maxDistance={15}
                                        // minPolarAngle={0}
                                        // maxPolarAngle={-90}
                                        // enableZoom={false}
                                        enablePan={false}
                                    />
                                </Suspense>
                            </Canvas>
                            <Loader />
                        </Suspense>
                    </div>
                </div>
            )}
        </>
    );
};

const OverallLengthDimension = () => {
    const { outerDiameter, overallLength, hasFlange, flangeDiameter } = QuickTurnPartStore;

    const gap = 0.05;
    const spacing = 0.25;

    return (
        <>
            <Line
                start={[0, hasFlange ? flangeDiameter / 2 + gap : outerDiameter / 2 + gap, 0]}
                end={[0, hasFlange ? flangeDiameter / 2 + spacing + 0.05 : outerDiameter / 2 + spacing + 0.05, 0]}
            />

            <Line
                start={[0, hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing, 0]}
                end={[0, hasFlange ? flangeDiameter / 2 + spacing + 0.05 : outerDiameter / 2 + spacing - 0.05, 0.05]}
            />

            <Line
                start={[0, hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing, 0]}
                end={[0, hasFlange ? flangeDiameter / 2 + spacing - 0.05 : outerDiameter / 2 + spacing + 0.05, 0.05]}
            />

            <Line
                start={[0, outerDiameter / 2 + gap, overallLength]}
                end={[
                    0,
                    hasFlange ? flangeDiameter / 2 + spacing + 0.05 : outerDiameter / 2 + spacing + 0.05,
                    overallLength,
                ]}
            />

            <Line
                start={[0, hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing, overallLength]}
                end={[
                    0,
                    hasFlange ? flangeDiameter / 2 + spacing + 0.05 : outerDiameter / 2 + spacing + 0.05,
                    overallLength - 0.05,
                ]}
            />

            <Line
                start={[0, hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing, overallLength]}
                end={[
                    0,
                    hasFlange ? flangeDiameter / 2 + spacing - 0.05 : outerDiameter / 2 + spacing - 0.05,
                    overallLength - 0.05,
                ]}
            />

            <Line
                start={[0, hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing, 0]}
                end={[
                    0,
                    hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing,
                    overallLength > 0.25 / 2 ? overallLength / 2 - 0.25 : 0,
                ]}
            />

            <Line
                start={[
                    0,
                    hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing,
                    overallLength > 0.25 / 2 ? overallLength / 2 + 0.25 : 0,
                ]}
                end={[0, hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing, overallLength]}
            />

            <Html
                scale={2}
                rotation={[Math.PI / 2, 0, 0]}
                position={[
                    0,
                    hasFlange ? flangeDiameter / 2 + spacing : outerDiameter / 2 + spacing,
                    overallLength / 2,
                ]}
                // transform
                // occlude
            >
                <div className="annotation">Overall Length</div>
            </Html>
        </>
    );
};

const FlangeThicknessDimension = () => {
    const { flangeThickness, flangeDiameter } = QuickTurnPartStore;

    const gap = 0.05;
    const spacing = 0.5;

    return (
        <>
            <Line start={[0, flangeDiameter / 2 + gap, 0]} end={[0, flangeDiameter / 2 + spacing + 0.05, 0]} />

            <Line start={[0, flangeDiameter / 2 + spacing, 0]} end={[0, flangeDiameter / 2 + spacing + 0.05, 0.05]} />

            <Line start={[0, flangeDiameter / 2 + spacing, 0]} end={[0, flangeDiameter / 2 + spacing - 0.05, 0.05]} />

            <Line
                start={[0, flangeDiameter / 2 + gap, flangeThickness]}
                end={[0, flangeDiameter / 2 + spacing + 0.05, flangeThickness]}
            />

            <Line
                start={[0, flangeDiameter / 2 + spacing, flangeThickness]}
                end={[0, flangeDiameter / 2 + spacing + 0.05, flangeThickness - 0.05]}
            />

            <Line
                start={[0, flangeDiameter / 2 + spacing, flangeThickness]}
                end={[0, flangeDiameter / 2 + spacing - 0.05, flangeThickness - 0.05]}
            />

            <Line
                start={[0, flangeDiameter / 2 + spacing, 0]}
                end={[0, flangeDiameter / 2 + spacing, flangeThickness > 0.25 / 2 ? flangeThickness / 2 - 0.25 : 0]}
            />

            <Line
                start={[0, flangeDiameter / 2 + spacing, flangeThickness > 0.25 / 2 ? flangeThickness / 2 + 0.25 : 0]}
                end={[0, flangeDiameter / 2 + spacing, flangeThickness]}
            />

            <Html
                scale={2}
                rotation={[Math.PI / 2, 0, 0]}
                position={[0, flangeDiameter / 2 + spacing, flangeThickness / 2]}
                // transform
                // occlude
            >
                <div className="annotation">Flange Length</div>
            </Html>
        </>
    );
};

const OuterDiameterDimension = () => {
    const { outerDiameter, overallLength, hasThruHole } = QuickTurnPartStore;

    const gap = 0.05;
    const spacing = 1;
    const arrowSize = 0.05;

    return (
        <>
            <Line
                start={[0, outerDiameter / 2, overallLength + gap, 0]}
                end={[0, outerDiameter / 2, overallLength + spacing + arrowSize, 0]}
            />
            <Line
                start={[0, -outerDiameter / 2, overallLength + gap, 0]}
                end={[0, -outerDiameter / 2, overallLength + spacing + arrowSize, 0]}
            />

            <Line start={[0, outerDiameter / 2, overallLength + spacing]} end={[0, 0.1, overallLength + spacing]} />
            <Line
                start={[0, outerDiameter / 2, overallLength + spacing]}
                end={[0, outerDiameter / 2 - arrowSize, overallLength + spacing + arrowSize]}
            />
            <Line
                start={[0, outerDiameter / 2, overallLength + spacing]}
                end={[0, outerDiameter / 2 - arrowSize, overallLength + spacing - arrowSize]}
            />

            <Line start={[0, -outerDiameter / 2, overallLength + spacing]} end={[0, -0.1, overallLength + spacing]} />

            <Line
                start={[0, -outerDiameter / 2, overallLength + spacing]}
                end={[0, -outerDiameter / 2 + arrowSize, overallLength + spacing + arrowSize]}
            />
            <Line
                start={[0, -outerDiameter / 2, overallLength + spacing]}
                end={[0, -outerDiameter / 2 + arrowSize, overallLength + spacing - arrowSize]}
            />

            <Html
                scale={2}
                rotation={[Math.PI / 2, 0, 0]}
                position={[0, 0, overallLength + spacing]}
                // transform
                // occlude
            >
                <div className="annotation">Outer Diameter</div>
            </Html>
        </>
    );
};

const FlangeDiameterDimension = () => {
    const { flangeDiameter, overallLength } = QuickTurnPartStore;

    const gap = 0.05;
    const spacing = 0.5;
    const arrowSize = 0.05;

    return (
        <>
            <Line
                start={[0, flangeDiameter / 2, 0 - gap, 0]}
                end={[0, flangeDiameter / 2, 0 - spacing + arrowSize, 0]}
            />
            <Line
                start={[0, -flangeDiameter / 2, 0 - gap, 0]}
                end={[0, -flangeDiameter / 2, 0 - spacing + arrowSize, 0]}
            />

            <Line start={[0, flangeDiameter / 2, 0 - spacing]} end={[0, 0.1, 0 - spacing]} />
            <Line
                start={[0, flangeDiameter / 2, 0 - spacing]}
                end={[0, flangeDiameter / 2 - arrowSize, 0 - spacing + arrowSize]}
            />
            <Line
                start={[0, flangeDiameter / 2, 0 - spacing]}
                end={[0, flangeDiameter / 2 - arrowSize, 0 - spacing - arrowSize]}
            />

            <Line start={[0, -flangeDiameter / 2, 0 - spacing]} end={[0, -0.1, 0 - spacing]} />

            <Line
                start={[0, -flangeDiameter / 2, 0 - spacing]}
                end={[0, -flangeDiameter / 2 + arrowSize, 0 - spacing + arrowSize]}
            />
            <Line
                start={[0, -flangeDiameter / 2, 0 - spacing]}
                end={[0, -flangeDiameter / 2 + arrowSize, 0 - spacing - arrowSize]}
            />

            <Html
                scale={2}
                rotation={[Math.PI / 2, 0, 0]}
                position={[0, 0, -spacing]}
                // transform
                // occlude
            >
                <div className="annotation">Flange Diameter</div>
            </Html>
        </>
    );
};

const InnerDiameterDimension = () => {
    const { innerDiameter, overallLength } = QuickTurnPartStore;

    const gap = 0.05;
    const spacing = 0.375;
    const arrowSize = 0.05;

    return (
        <>
            <Line
                start={[0, innerDiameter / 2, overallLength + gap, 0]}
                end={[0, innerDiameter / 2, overallLength + spacing + arrowSize, 0]}
            />
            <Line
                start={[0, -innerDiameter / 2, overallLength + gap, 0]}
                end={[0, -innerDiameter / 2, overallLength + spacing + arrowSize, 0]}
            />

            <Line start={[0, innerDiameter / 2, overallLength + spacing]} end={[0, 0.1, overallLength + spacing]} />
            <Line
                start={[0, innerDiameter / 2, overallLength + spacing]}
                end={[0, innerDiameter / 2 - arrowSize, overallLength + spacing + arrowSize]}
            />
            <Line
                start={[0, innerDiameter / 2, overallLength + spacing]}
                end={[0, innerDiameter / 2 - arrowSize, overallLength + spacing - arrowSize]}
            />

            <Line start={[0, -innerDiameter / 2, overallLength + spacing]} end={[0, -0.1, overallLength + spacing]} />

            <Line
                start={[0, -innerDiameter / 2, overallLength + spacing]}
                end={[0, -innerDiameter / 2 + arrowSize, overallLength + spacing + arrowSize]}
            />
            <Line
                start={[0, -innerDiameter / 2, overallLength + spacing]}
                end={[0, -innerDiameter / 2 + arrowSize, overallLength + spacing - arrowSize]}
            />

            <Html
                scale={2}
                rotation={[Math.PI / 2, 0, 0]}
                position={[0, 0, overallLength + spacing]}
                // transform
                // occlude
            >
                <div className="annotation">Thru-Hole Diameter</div>
            </Html>
        </>
    );
};

function Line(props: { start: number[]; end: number[] }) {
    const ref = useRef<THREE.Line | any>();

    useFrame(() => {
        if (ref.current) {
            ref.current.geometry.setFromPoints([props.start, props.end].map(point => new THREE.Vector3(...point)));
        }
    });
    return (
        <line ref={ref}>
            <bufferGeometry />
            <lineBasicMaterial color="white" linewidth={4} />
        </line>
    );
}

export default observer(ThreeJSCanvas);
